// Локальная база клиента (IndexedDB через Dexie).
//
// Состав таблиц — `offline-first.md` §3. Заводятся ВМЕСТЕ с кодом, который их читает
// и пишет: пустая схема каркаса (T-008) существовала ровно затем, чтобы не разъехаться
// с реальностью до первого релиза.
//
// Scope всего кэша — ПАРА (точка, человек). Точка приходит из сессии вошедшего, а не
// из устройства: решение T3A отменено (T-018), у каждой роли есть логин, и человек
// приносит свою точку с собой. Оба идентификатора обязательны — у сотрудника может быть
// две точки, а на одном моноблоке за смену работают несколько поваров.
//
// Правило версионирования: каждое изменение набора таблиц или индексов — НОВЫЙ вызов
// .version(N).stores({...}). Молча править существующую версию нельзя: у пользователей
// на устройствах уже лежит база предыдущей версии, и Dexie применяет апгрейды
// по номерам версий.

import Dexie, { type Table } from 'dexie';

export const OFFLINE_DB_NAME = 'restomarker';

/**
 * Версия схемы.
 *
 * 3 — снятие таблиц, которых никто не читал (T-018, шаг 10). Шесть store'ов
 * (`products`, `categories`, `employees`, `device`, `location`, `fifoCounters`) были
 * объявлены каркасом и не получили НИ ОДНОЙ записи — реальные кэши осели
 * в `@capacitor/preferences`.
 *
 * 4 — локальные копии напечатанных этикеток (T-036). До неё офлайн держался ровно
 * до первой операции над уже напечатанной этикеткой: журнал и мониторинг без сети
 * пусты, списать/перепечатать нечего, просрочку не по чему считать.
 *
 * 5 — key-value для персиста кэша TanStack Query (фаза 3, оффлайн кабинета).
 * Кабинет (каталог, персонал, точки) жил только в памяти: перезапуск без сети
 * отрисовывал пустоту. Теперь снимок кэша переживает перезапуск.
 *
 * 6 — очередь мутаций кабинета (фаза 3, этап B). До неё правки каталога уходили
 * прямым POST и без сети просто падали: посмотреть меню офлайн стало можно (v5),
 * а изменить — нет.
 */
export const OFFLINE_DB_VERSION = 6;

export type QueuedOpType = 'print' | 'void' | 'dispose' | 'undispose' | 'reprint';
export type QueuedOpStatus = 'pending' | 'syncing' | 'error';

export type QueuedOp = {
  /** UUID, идемпотентный ключ. Для `print` он же становится id PrintJob. */
  clientId: string;
  type: QueuedOpType;
  locationId: string;
  /** Автор, зафиксированный В МОМЕНТ ТАПА. См. `enqueue` — там объяснение. */
  userId: string;
  payload: Record<string, unknown>;
  /** Момент тапа. По нему валидируется окно ≤5 минут — и клиентом, и сервером. */
  actedAt: string;
  createdAt: string;
  attempts: number;
  status: QueuedOpStatus;
  lastError?: string;
};

/**
 * Профиль вошедшего и статус подписки. Ключ один — сессия на устройстве одна (Д3).
 *
 * **Профиль здесь несущий, а не вспомогательный.** Кэш TanStack Query живёт только
 * в памяти, а из профиля берутся ЛИЧНОСТЬ автора печати и ТАЙМЗОНА точки, в которой
 * считается срок годности. Без него приложение, перезапущенное без сети, не может
 * печатать вовсе — прямое нарушение инварианта 3.
 *
 * До T-018 личность лежала в Preferences (выбранная карточка) и перезапуск переживала;
 * переезд на сессию это свойство отнял, и вернуть его — работа этой таблицы.
 */
export type SessionCache = {
  key: 'current';
  /**
   * Профиль ЦЕЛИКОМ, в форме ответа `/tenants/me/`.
   *
   * Объектом, а не полем-в-поле. Разложенная копия расходится с контрактом при первой
   * же правке сериализатора: сервер добавляет поле, экран его читает, а с диска
   * приходит `undefined` — и обнаруживается это не компилятором, а пустым местом
   * на экране у того, кто работает без сети.
   */
  profile: unknown;
  /** Расхождение часов клиента и сервера, секунды. Меряется на каждом синке. */
  clockOffset: number;
  lastSyncAt: string | null;
  // Токен сессии здесь НЕ хранится: IndexedDB переживает смену origin
  // и синхронизируется, а секрету там не место (решение T-009).
};

/**
 * Локальная копия напечатанной этикетки (T-036, Р12).
 *
 * Форма — `PrintJob` из ответа API целиком, объектом, по той же причине, что
 * `SessionCache.profile`: разложенная копия расходится с контрактом при первой же
 * правке сериализатора.
 *
 * Scope — пара (точка, человек), как у меню. Оба обязательны: у повара может быть
 * две точки, а на одном моноблоке за смену работают несколько человек, и записи
 * предыдущего не должны подставляться следующему (`offline-first.md` §3).
 *
 * Ключ хранилища — сама тройка, чтобы `bulkPut` обновлял, а не плодил дубликаты
 * при каждой онлайн-загрузке журнала.
 */
/** Простая пара ключ-значение. Сейчас — снимок кэша TanStack Query (фаза 3). */
export type KeyValue = {
  key: string;
  value: unknown;
};

/**
 * Сущности кабинета, правки которых переживают отсутствие сети (фаза 3, этап B).
 *
 * `staff` добавлен точечно (фаза 3, этап B, дополнение): только правка карточки
 * и блокировка/разблокировка. Создание сотрудника офлайн НЕ покрыто и не будет —
 * пароль сотрудника показывается менеджеру РОВНО ОДИН РАЗ на экране создания
 * (`StaffFormDialog`), а очередь отправляет правку позже, когда этого экрана уже
 * нет. Заведённая офлайн учётка была бы создана без единого способа узнать её
 * пароль — то есть создана нерабочей. `useCreateLocation` по той же причине
 * (тяжёлый серверный побочный эффект — клонирование каталога сотен позиций,
 * а не создание одной записи) и `useCreateDevice` (нет экрана-потребителя)
 * в офлайн-кабинет не идут вовсе.
 */
export type CabinetEntity = 'category' | 'masterProduct' | 'locationProduct' | 'staff';
export type CabinetAction = 'create' | 'update' | 'delete' | 'addFromCatalog' | 'toggleBlock';

/**
 * Отложенная правка каталога.
 *
 * Отдельно от `syncQueue` (кухня): у кухонных операций свой автор в момент тапа,
 * своё окно ≤5 минут и схлопывание пар — кабинету это не нужно, а его CRUD
 * над сущностями кухонному движку чужд. Идемпотентность общая: `clientId` для
 * create становится id сущности на сервере.
 */
export type CabinetOp = {
  clientId: string;
  entity: CabinetEntity;
  action: CabinetAction;
  /** id сущности для update/delete; `null` для create и addFromCatalog. */
  targetId: string | null;
  /** Тело запроса как есть (`body` формы, список id для addFromCatalog и т.п.). */
  payload: Record<string, unknown>;
  /** Точка — для create продукта точки и addFromCatalog. */
  locationId: string | null;
  /**
   * `updated_at` сущности на момент начала правки. По нему сервер видит, что
   * кто-то изменил запись, пока автор был офлайн (last-write-wins + пометка).
   */
  baseUpdatedAt: string | null;
  createdAt: string;
  attempts: number;
  status: QueuedOpStatus;
  lastError?: string;
};

export type CachedLabel = {
  /** `${locationId}::${userId}::${jobId}` — тройка из §3. */
  scopedId: string;
  locationId: string;
  userId: string;
  jobId: string;
  /** Хранимый статус, НЕ предикат просрочки — по нему считается FIFO. */
  status: string;
  /** Продукт точки: по нему FIFO ведёт независимые ряды. */
  locationProductId: string;
  fifoNumber: number;
  /** Момент печати — по нему чистим кэш старше окна офлайна. */
  printedAt: string;
  /** `PrintJob` целиком, как отдал сервер. */
  job: unknown;
  cachedAt: string;
};

export class OfflineDatabase extends Dexie {
  syncQueue!: Table<QueuedOp, string>;
  session!: Table<SessionCache, string>;
  labels!: Table<CachedLabel, string>;
  kv!: Table<KeyValue, string>;
  cabinetQueue!: Table<CabinetOp, string>;

  constructor(name: string = OFFLINE_DB_NAME) {
    super(name);

    // `null` в апгрейде удаляет store. Шесть таблиц сняты вместе с версией 3:
    // ни одна не получила ни одной записи, а реальные кэши осели в Preferences.
    this.version(3).stores({
      products: null,
      categories: null,
      employees: null,
      device: null,
      location: null,
      fifoCounters: null,
      // Индекс по createdAt: флаш идёт строго FIFO, и сортировка в памяти
      // на очереди из сотен операций — лишняя работа на кухонном планшете.
      syncQueue: 'clientId, createdAt, status',
      session: 'key',
    });

    // 4 — локальные копии этикеток (T-036). Составной индекс `[locationId+userId]`
    // читает весь scope одним запросом; `[locationId+userId+locationProductId]` —
    // ряд одного продукта для вывода FIFO.
    this.version(4).stores({
      syncQueue: 'clientId, createdAt, status',
      session: 'key',
      labels: 'scopedId, [locationId+userId], [locationId+userId+locationProductId], printedAt',
    });

    // 5 — `kv` под снимок кэша TanStack Query (фаза 3). Ключ — сам ключ пары.
    this.version(5).stores({
      syncQueue: 'clientId, createdAt, status',
      session: 'key',
      labels: 'scopedId, [locationId+userId], [locationId+userId+locationProductId], printedAt',
      kv: 'key',
    });

    // 6 — очередь мутаций кабинета (фаза 3, этап B). Индекс по createdAt: флаш
    // строго FIFO (create раньше update над той же записью).
    this.version(6).stores({
      syncQueue: 'clientId, createdAt, status',
      session: 'key',
      labels: 'scopedId, [locationId+userId], [locationId+userId+locationProductId], printedAt',
      kv: 'key',
      cabinetQueue: 'clientId, createdAt, status',
    });
  }
}

export const offlineDb = new OfflineDatabase();
